import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import ApplicationFormContext from './ApplicationFormContext';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import { useApi } from '../ApiContext';
import { fetchDynamicLists, fetchFilterSearchRecord, fetchSearchSelectSavedValues, fetchValuesOnSearch } from '../Helpers/dataAccessFetch';
import { CONSTANTS, routes, fieldIdMappings } from '../Helpers/Configuration';
import { useParams, useHistory } from 'react-router-dom';
import ApplicationFormLayout from './ApplicationFormLayout';
import useRoot from '../RootContext/useRoot';
import PageLoadIndicator from '../Helpers/PageLoadIndicator';
import { executor, clearValuesForUnmetConditions } from '@nsr/react-query-builder-executor';
import parseTheme from '../../utilities/parseTheme';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { clearForm, clearForceShowErrors, Form as ReduxForm, setInitialValues } from '@nsr/react-form';
import settingsReducer, { setSettingsValue } from './redux/slices/settingsSlice';
import applicationReducer, { setApplication, setStatus, setApplicationValue } from './redux/slices/applicationSlice';
import { batch, useDispatch, useSelector } from 'react-redux';
import templateReducer, { setTemplate as setTemplateAction } from './redux/slices/templateSlice';
import isLoadingReducer, { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice.js';
import simulateAssignSupplementalItems from '../../utilities/simulateAssignSupplementalItems';
import { EDSApplication } from '@ellucian/react-design-system/core';
import { ContentActionOptions } from '../Admin/ConditionalLogicDialog';
import { cleanseLists } from '../Helpers/ApplicationHelpers';
import { loadReCaptcha } from 'react-recaptcha-v3-updated';
import rtlDetect from 'rtl-detect';
import { fetchCurriculum, autosaveAction } from './redux/actions/applicationActions';
import { isInquiryFormTemplate, isSupplementalFormTemplate } from '@nsr/applicant-apply-shared/src/helpers/applicationTemplateHelper.js';
import Timeout from '../Timeout/Timeout.jsx';
const { applicationFeeAssignmentType, applicationFormStatuses, inquiryTypes } = CONSTANTS;

const ApplicationWrapper = props => {
    const additionalSlices = useMemo(() => ({
        application: applicationReducer,
        isLoading: isLoadingReducer,
        settings: settingsReducer,
        template: templateReducer,
    }), []);
    return (
        <ReduxForm createNewStore={true} additionalSlices={additionalSlices}>
            <Application {...props} />
        </ReduxForm>
    );
};
ApplicationWrapper.propTypes = {
    children: PropTypes.node,
};

const selectRevision = state => state.settings.revision;
const selectApplication = state => state.application;
const selectValues = state => state.form.values;

const Application = props => {
    const intl = useIntl();
    const {
        externalMode,
        onLeavePreview,
        previewMode = false,
        template: templateProp,
        isTimedout,
        adminApi,
        adminTenant,
        isAps
    } = props;
    const api = adminApi || useApi();
    const rootCtx = useRoot();
    const snackbar = useSnackbar();
    const routeParams = useParams();
    const history = useHistory();
    const [template, setTemplate] = useState(null);
    const [theme, setTheme] = useState(null);
    const [formMessages, setFormMessages] = useState(null);
    const [selectedLocale, setSelectedLocale] = useState(null);
    const [prospect, setProspect] = useState(null);
    const [hasTimedout, setHasTimedout] = useState(false);
    const { globalLocale, localeDirection } = useContext(GlobalLocaleContext);
    const [selectedLocaleDirection, setSelectedLocaleDirection] = useState(localeDirection);
    const dispatch = useDispatch();
    const application = useSelector(selectApplication);
    const applicationRevision = useSelector(selectRevision);
    const applicationValues = useSelector(selectValues);
    const setInitialLocale = (template) => {
        let selectedLocale;
        selectedLocale = globalLocale || template.defaultLanguage;
        setSelectedLocale(selectedLocale);
        setSelectedLocaleDirection(rtlDetect.isRtlLang(selectedLocale) ? 'rtl' : 'ltr');
    };
    const tenant = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const { supportedGraphqlModels } = tenant;
    const { recaptcha } = tenant;
    const inquiryMode = useMemo(() => {
        return isInquiryFormTemplate(template?.templateType);
    }, [template]);
    const applicationRef = useRef();
    applicationRef.current = application;

    useEffect(() => {
        if (!previewMode && recaptcha?.recaptchaToggle && recaptcha?.recaptchaClientToken) {
            loadReCaptcha(recaptcha.recaptchaClientToken, onLoadCallback);
        }
    }, [previewMode, recaptcha]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [history.location.pathname]);

    useEffect(() => {
        if (!application || !template) {
            dispatch(startLoading());
        }
        else {
            dispatch(stopLoading());
        }
    }, [application, template]);

    useEffect(() => {
        if (isTimedout && externalMode) {
            batch(() => {
                dispatch(setInitialValues({
                    initialValues: {},
                }));
                dispatch(clearForceShowErrors());
                dispatch(clearForm());
            });
        }
    }, [isTimedout]);

    useEffect(() => {
        // Check for pages to be hidden based on conditional logic
        const fn = async () => {
            if (template) {
                let pagesToHide = [];
                for (const page of template.pages) {
                    if (page.conditions?.length) {
                        const { fn = null, query, effect } = page.conditions[0];
                        const evaluateCondition = fn || executor(query);
                        const result = await evaluateCondition(applicationValues);
                        if (result && effect === ContentActionOptions.hide || !result && effect === ContentActionOptions.show) {
                            pagesToHide.push(page.id);
                        }
                    }
                }
                dispatch(setSettingsValue({
                    name: 'hiddenPageIds',
                    value: pagesToHide,
                }));
            }
        };
        fn();
    }, [applicationValues]);

    useEffect(() => {
        batch(() => {
            dispatch(setSettingsValue({
                name: 'externalMode',
                value: !!externalMode,
            }));
            dispatch(setSettingsValue({
                name: 'previewMode',
                value: !!previewMode,
            }));
        });

        const determineTheme = async template => {
            // Set the theme in local state to the global theme OR the template's theme
            // Only call rootCtx.setTheme if the template has a theme since otherwise it is already set to global.
            if (template?.theme) {
                try {
                    const eteTheme = await api.theme.get(template.theme, tenant.themeAppId);
                    if (eteTheme) {
                        rootCtx.setTheme(parseTheme(eteTheme), eteTheme);
                        setTheme(eteTheme);
                    }
                } catch (e) {
                    console.error(e);
                    setTheme(rootCtx.eteTheme || {});
                }
            } else {
                setTheme(rootCtx.eteTheme || {});
            }
            return;
        };

        const compileConditionalLogic = template => {
            const compile = condition => Object.assign({}, condition, {
                fn: executor(condition.query),
            });
            forEach(template.pages, page => {
                if (page.conditions) {
                    page.conditions = page.conditions.map(compile);
                }
                forEach(page.sections, section => {
                    if (section.conditions) {
                        section.conditions = section.conditions.map(compile);
                    }
                    forEach(section.groups, group => {
                        forEach(group.questions, question => {
                            if (question.conditions && !section.showRepetition) {
                                question.conditions = question.conditions.map(compile);
                            }
                        });
                    });
                });
            });
            return template;
        };

        const fn = async () => {
            try {
                const { id: applicationId } = routeParams;
                let application;
                if (previewMode || externalMode) {
                    application = {
                        id: applicationId,
                        status: CONSTANTS.applicationFormStatuses.STARTED,
                        pageStatuses: {},
                        templateId: templateProp.id,
                        values: {},
                    };
                }
                else {
                    application = await api.applicationForms.getById(applicationId);
                    if (application.status === CONSTANTS.applicationFormStatuses.NOTSTARTED) {
                        try {
                            const updatedApp = Object.assign({}, application, {
                                status: CONSTANTS.applicationFormStatuses.STARTED,
                            });
                            application = await api.applicationForms.update(updatedApp);
                        } catch (err) {
                            console.error(err);
                            snackbar.show(intl.formatMessage({ id: 'generalErrorMessage' }));
                        }
                    }
                    if (application.notifyTemplateUpdate) {
                        try {
                            dispatch(setSettingsValue({
                                name: 'notifyTemplateUpdate',
                                value: application.notifyTemplateUpdate,
                            }));
                            application = await api.applicationForms.validateApplication(application);
                        } catch (err) {
                            console.error(err);
                            snackbar.show(intl.formatMessage({ id: 'generalErrorMessage' }));
                        }
                    }
                }
                let template = templateProp;
                if (!template) {
                    template = await api.applicationTemplates.getById(application.templateId);
                }
                const dataAccessErrors = [
                    await fetchDynamicLists(
                        api.dataAccess, template, supportedGraphqlModels
                    ),
                    await fetchSearchSelectSavedValues(
                        api.dataAccess, template, application, supportedGraphqlModels
                    ),
                    await fetchFilterSearchRecord(
                        api.dataAccess, template, application?.values, supportedGraphqlModels
                    )
                ];

                if (dataAccessErrors.some(errorList => !!errorList.length)) {
                    snackbar.show(intl.formatMessage({ id: 'dataAccessListErrorText' }));
                }

                cleanseLists(template, application);
                await determineTheme(template);
                setTemplate(compileConditionalLogic(template));
                batch(() => {
                    dispatch(setSettingsValue({
                        name: 'inquiryMode',
                        value: isInquiryFormTemplate(template?.templateType),
                    }));
                    dispatch(setSettingsValue({
                        name: 'supplementalFormMode',
                        value: isSupplementalFormTemplate(template?.templateType),
                    }));
                    dispatch(setSettingsValue({
                        name: 'revision',
                        value: application.revision,
                    }));
                    dispatch(setApplication(application));
                    dispatch(setTemplateAction(compileConditionalLogic(template)));
                    dispatch(setInitialValues({
                        initialValues: application.values
                    }));
                    dispatch(fetchCurriculum({ api }));
                });
                setInitialLocale(template);
            } catch (e) {
                console.error(e);
                history.push(routes.home);
            }
        };
        fn();
    }, []);

    useEffect(() => {
        if (selectedLocale) {
            let messages = get(template, `templateMessages.languages.${selectedLocale}.messages`, {});
            if (Object.keys(messages).length) {
                setFormMessages(Object.assign({}, intl.messages, messages));
            }
            else {
                messages = get(template, `templateMessages.languages.${template.defaultLanguage}.messages`, {});
                setFormMessages(Object.assign({}, intl.messages, messages));
            }
        }
    }, [selectedLocale, intl.messages]);

    useEffect(() => {
        if (globalLocale && template) {
            let messages = get(template, `templateMessages.languages.${globalLocale}.messages`, {});
            if (Object.keys(messages).length) {
                setSelectedLocale(globalLocale);
                setSelectedLocaleDirection(localeDirection);
                setFormMessages(Object.assign({}, intl.messages, messages));
            }
            else {
                setSelectedLocale(template.defaultLanguage);
                setSelectedLocaleDirection(rtlDetect.isRtlLang(template.defaultLanguage) ? 'rtl' : 'ltr');
            }
        }
    }, [globalLocale, selectedLocale, template?.id]);

    const supplementalFormMode = useMemo(() => {
        return isSupplementalFormTemplate(template?.templateType);
    }, [template]);

    const autosave = useCallback(async values => {
        if (!previewMode && !externalMode && !inquiryMode) {
            rootCtx.setIsAutosaving(true);
            try {
                const result = await dispatch(autosaveAction({ values })).unwrap();
                rootCtx.setIsAutosaving(false);

                return result;
            } catch (err) {
                console.error(err);
                rootCtx.setIsAutosaving(false);
            }
        }
    }, [previewMode, externalMode, inquiryMode]);

    const savePayment = useCallback(async paymentInformation => {
        if (!previewMode && !inquiryMode) {
            const newApplication = Object.assign({}, application, { paymentInformation, revision: applicationRevision });
            const result = await api.applicationForms.savePayment(newApplication);
            batch(() => {
                dispatch(setSettingsValue({
                    name: 'revision',
                    value: result.revision,
                }));
                dispatch(setApplicationValue({
                    name: 'paymentInformation',
                    value: result.paymentInformation,
                }));
            });
            return result;
        }
    }, [application, previewMode, externalMode, inquiryMode]);

    const getRedirectUrl = useCallback(async () => {
        const response = await api.payments.getRedirectUrl(application);
        return response;
    }, [application]);

    const getPaymentStatus = useCallback(async () => {
        if (application?.paymentInformation) {
            const response = await api.payments.getPaymentStatus(application);
            const latestApplication = await api.applicationForms.getById(application.id);
            dispatch(setApplication(latestApplication));
            return response;
        }
    }, [application]);

    const onLoadCallback = useCallback(() => {
        let component = document.getElementsByName('g-recaptcha-response');
        let textAreas = [...component];
        textAreas.map(textArea => {
            textArea.setAttribute('aria-hidden', 'true');
            textArea.setAttribute('aria-label', 'do not use');
            textArea.setAttribute('aria-readonly', 'true');
        });
    }, []);

    const validateDiscountCode = useCallback(async value => {
        try {
            const result = await api.applicationForms.validateDiscountCode(value, application.id);
            if (result.valid) {
                return result.validDiscountCodeInfo;
            }
            else {
                return false;
            }
        }
        catch (err) {
            console.error(err);
            if (!previewMode) {
                snackbar.show(intl.formatMessage({ id: 'generalErrorMessage' }));
            }
        }
    }, [application]);

    const handleReset = () => {
        if (hasTimedout) {
            setHasTimedout(false);
        }
    };

    const forceTimeout = async () => {
        await api.applicantLogout();
        if (!hasTimedout){
            setHasTimedout(true);
        }
    };

    const applicationFormCtx = {
        autosave,
        adminTenant,
        adminApi,
        clearApplicationSession: () => {
            setTemplate(null);
            setTheme({});
            batch(() => {
                dispatch(setApplication(null));
                dispatch(setInitialValues({
                    initialValues: {},
                }));
                dispatch(clearForceShowErrors());
                dispatch(clearForm());
            });
            rootCtx.clearTheme();
        },
        clearForceShowErrors: () => {
            dispatch(clearForceShowErrors());
        },
        externalMode: !!externalMode,
        fetchValuesOnSearch: (...args) => fetchValuesOnSearch(api.dataAccess, ...args, supportedGraphqlModels)
            .catch(() => {
                snackbar.show(intl.formatMessage({ id: 'an_unexpected_error_occurred' }));
            }),
        formMessages,
        getApplicationFee: async appValues => {
            if ((application?.status && ![applicationFormStatuses.NOTSTARTED, applicationFormStatuses.STARTED].includes(application.status)) ||
                !template?.settings?.paymentInformation?.applicationFees?.length) {
                return null;
            }
            for (const fee of template.settings.paymentInformation.applicationFees) {
                if (fee.assignmentType === applicationFeeAssignmentType.all) {
                    return fee;
                }
                if (fee.assignmentType === applicationFeeAssignmentType.query && fee.query) {
                    const fn = executor(fee.query);
                    const result = await fn(appValues);
                    if (result) {
                        return fee;
                    }
                }
            }
            return null;
        },
        globalSettings: tenant,
        inquiryMode,
        isSubmitted: () => application.status === applicationFormStatuses.SUBMITTED,
        onLeavePreview,
        previewMode: !!previewMode,
        isAps: !!isAps,
        isPaymentPending: () => application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending,
        prospect,
        getRedirectUrl,
        getPaymentStatus,
        savePayment,
        selectedLocale,
        setProspect,
        setSelectedLocale,
        submit: async appValues => {
            try {
                const values = await clearValuesForUnmetConditions(template, appValues);
                let latestApplication = inquiryMode ? applicationRef.current : await api.applicationForms.getById(applicationRef.current.id);
                const submitDate = new Date();
                const updatedApplication = Object.assign({}, latestApplication, {
                    revision: applicationRevision,
                    status: applicationFormStatuses.SUBMITTED,
                    submittedOn: submitDate,
                    values,
                    locale: selectedLocale
                });
                let result;
                if (externalMode) {
                    delete updatedApplication.id;
                }
                if (previewMode) {
                    result = await simulateAssignSupplementalItems(updatedApplication, template);
                } else if (inquiryMode) {
                    const inquiryValues = updatedApplication.values;
                    let inquiryStatus = inquiryTypes.app;
                    for (const [, value] of Object.entries(inquiryValues)) {
                        if (has(value, fieldIdMappings.inquiryStatusId)) {
                            inquiryStatus = value[`${fieldIdMappings.inquiryStatusId}`];
                            break;
                        }
                    }
                    updatedApplication.status = inquiryStatus;
                    let recaptchaToken;
                    if (recaptcha?.recaptchaToggle && recaptcha?.recaptchaClientToken && window.grecaptcha !== undefined) {
                        try {
                            recaptchaToken = await window.grecaptcha.execute(recaptcha?.recaptchaClientToken, { action: CONSTANTS.recaptchaActions.inquiry });
                        } catch (e) {
                            console.error(e);
                        }
                    }
                    result = await api.inquiries.createInquiry(updatedApplication, recaptchaToken, CONSTANTS.recaptchaActions.inquiry);
                } else {
                    result = await api.applicationForms.update(updatedApplication);
                }
                batch(() => {
                    dispatch(setStatus(result.status));
                    dispatch(setApplication(result));
                });
                return Promise.resolve(result);
            } catch (e) {
                console.error(e);
                return Promise.reject(e);
            }
        },
        supplementalFormMode,
        template,
        updateValues: newValues => {
            dispatch(setInitialValues(cloneDeep(newValues)));
        },
        validateDiscountCode,
    };
    return (
        <ApplicationFormContext.Provider value={applicationFormCtx}>
            {application && template &&
                <EDSApplication
                    theme={theme}
                    locale={selectedLocale}
                    dir={selectedLocaleDirection}
                    labelOverrides={{
                        'component.Search.noresult': intl.formatMessage({ id: 'noSearchResultsTextId' }),
                    }}
                >
                    <ApplicationFormLayout />
                </EDSApplication>
            }
            <PageLoadIndicator />
            <Timeout 
                forceTimeout={forceTimeout}
                isTimedout={hasTimedout}
                handleReset={handleReset}
            />
        </ApplicationFormContext.Provider>
    );
};
Application.propTypes = {
    externalMode: PropTypes.bool,
    onLeavePreview: PropTypes.func,
    previewMode: PropTypes.bool,
    template: PropTypes.object,
    isTimedout: PropTypes.bool,
    adminApi: PropTypes.object,
    adminTenant: PropTypes.object,
    isAps: PropTypes.bool,
};
export default ApplicationWrapper;